import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Check, 
  X, 
  Clock, 
  MessageSquare, 
  ChevronRight, 
  FileText,
  BookOpen,
  Database,
  Search,
  Eye
} from 'lucide-react';
import { useToast } from '../components/ui/use-toast';
import { useAuth } from '../context/auth-context';
import { API_URL } from '../config/api';

interface Topic {
  id: number;
  topic_name: string;
  topic_description: string;
  relevance_score: number;
  status: string;
  created_at: string;
  generation_metadata?: any;
  research_count?: number;
}

interface ResearchDocument {
  id: string;
  title: string;
  content: string;
  key_findings?: string;
  is_approved: boolean;
  created_at: string;
}

interface Site {
  id: string;
  name: string;
  url: string;
  sector_name?: string;
}

interface RAGDocument {
  id: string;
  name: string;
  content: string;
  type: string;
  relevance: number;
  keywords?: string[];
}

export default function SiteTopicsEnhanced() {
  const [topics, setTopics] = useState<Topic[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [selectedSite, setSelectedSite] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState<Topic | null>(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [researchDialogOpen, setResearchDialogOpen] = useState(false);
  const [ragDialogOpen, setRagDialogOpen] = useState(false);
  const [reviewAction, setReviewAction] = useState<'approve' | 'reject' | 'hold'>('approve');
  const [reviewNotes, setReviewNotes] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [topicResearch, setTopicResearch] = useState<ResearchDocument[]>([]);
  const [ragDocuments, setRagDocuments] = useState<RAGDocument[]>([]);
  const [loadingResearch, setLoadingResearch] = useState(false);
  const [loadingRAG, setLoadingRAG] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    loadUserSites();
  }, []);

  useEffect(() => {
    if (selectedSite) {
      loadTopics();
      loadRAGDocuments();
    }
  }, [selectedSite, statusFilter]);

  const loadUserSites = async () => {
    try {
      const response = await fetch(`${API_URL}/sites/my-sites`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error response:', errorData);
        throw new Error(errorData.error || 'Failed to fetch sites');
      }
      
      const data = await response.json();
      setSites(data);
      if (data.length > 0) {
        setSelectedSite(data[0].id);
      }
    } catch (error) {
      console.error('Error loading sites:', error);
      toast({
        title: 'Error',
        description: 'Failed to load your sites',
        variant: 'destructive',
      });
    }
  };

  const loadTopics = async () => {
    if (!selectedSite) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams({
        site_id: selectedSite,
        topic_type: 'site'
      });
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      
      const response = await fetch(`${API_URL}/topics?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch topics');
      }
      
      const data = await response.json();
      setTopics(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading topics:', error);
      setTopics([]);
      toast({
        title: 'Error',
        description: 'Failed to load topics',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadRAGDocuments = async () => {
    if (!selectedSite) return;
    
    setLoadingRAG(true);
    try {
      const response = await fetch(`${API_URL}/rag/sites/${selectedSite}/documents?includeResearch=true&limit=20`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setRagDocuments(data.documents || []);
      }
    } catch (error) {
      console.error('Error loading RAG documents:', error);
    } finally {
      setLoadingRAG(false);
    }
  };

  const loadTopicResearch = async (topicId: number) => {
    setLoadingResearch(true);
    try {
      const response = await fetch(`${API_URL}/topics/${topicId}/research`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        // Transform the data to match the expected ResearchDocument interface
        const transformedData = Array.isArray(data) ? data.map((item: any) => ({
          id: item.id,
          title: item.topic_name || 'Research Document',
          content: item.research_content || item.research_summary || '',
          key_findings: item.key_findings,
          is_approved: true,
          created_at: item.created_at
        })) : [];
        setTopicResearch(transformedData);
      } else {
        // If no research found, show empty state
        setTopicResearch([]);
      }
    } catch (error) {
      console.error('Error loading research:', error);
      toast({
        title: 'Error',
        description: 'Failed to load research documents',
        variant: 'destructive',
      });
      setTopicResearch([]);
    } finally {
      setLoadingResearch(false);
    }
  };

  const handleReview = (topic: Topic, action: 'approve' | 'reject' | 'hold') => {
    setSelectedTopic(topic);
    setReviewAction(action);
    setReviewNotes('');
    setReviewDialogOpen(true);
  };

  const handleViewResearch = async (topic: Topic) => {
    setSelectedTopic(topic);
    setResearchDialogOpen(true);
    await loadTopicResearch(topic.id);
  };

  const submitReview = async () => {
    if (!selectedTopic) return;

    try {
      const response = await fetch(`${API_URL}/topics/${selectedTopic.id}/review`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: reviewAction,
          notes: reviewNotes,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to review topic');
      }

      toast({
        title: 'Success',
        description: `Topic ${reviewAction}ed successfully`,
      });

      setReviewDialogOpen(false);
      loadTopics();
    } catch (error) {
      console.error('Error reviewing topic:', error);
      toast({
        title: 'Error',
        description: 'Failed to review topic',
        variant: 'destructive',
      });
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'outline' | 'destructive'> = {
      pending: 'default',
      approved: 'secondary',
      rejected: 'destructive',
      hold: 'outline',
      researching: 'default',
      completed: 'secondary',
    };

    const colors: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      hold: 'bg-gray-100 text-gray-800',
      researching: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
    };

    return (
      <Badge variant={variants[status] || 'default'} className={colors[status]}>
        {status}
      </Badge>
    );
  };

  const statsCards = [
    { 
      title: 'Pending', 
      count: topics.filter(t => t.status === 'pending').length,
      color: 'text-yellow-600',
      icon: Clock 
    },
    { 
      title: 'Approved', 
      count: topics.filter(t => t.status === 'approved').length,
      color: 'text-green-600',
      icon: Check 
    },
    { 
      title: 'Researching', 
      count: topics.filter(t => t.status === 'researching').length,
      color: 'text-blue-600',
      icon: Search 
    },
    { 
      title: 'Completed', 
      count: topics.filter(t => t.status === 'completed').length,
      color: 'text-purple-600',
      icon: BookOpen 
    },
  ];

  const getRAGTypeIcon = (type: string) => {
    switch (type) {
      case 'sector_topic':
      case 'site_topic':
        return <FileText className="h-4 w-4" />;
      case 'topic_research':
        return <BookOpen className="h-4 w-4" />;
      case 'customer_document':
        return <Database className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const getRAGTypeBadge = (type: string) => {
    const typeLabels: Record<string, string> = {
      'sector_topic': 'Sector Topic',
      'site_topic': 'Site Topic',
      'topic_research': 'Research',
      'customer_document': 'Customer Doc',
      'page_research': 'Page Research'
    };

    const typeColors: Record<string, string> = {
      'sector_topic': 'bg-blue-100 text-blue-800',
      'site_topic': 'bg-purple-100 text-purple-800',
      'topic_research': 'bg-green-100 text-green-800',
      'customer_document': 'bg-orange-100 text-orange-800',
      'page_research': 'bg-pink-100 text-pink-800'
    };

    return (
      <Badge className={typeColors[type] || 'bg-gray-100 text-gray-800'}>
        {typeLabels[type] || type}
      </Badge>
    );
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Site Topics & Research</h1>
          <p className="text-muted-foreground mt-2">
            Manage topics and view research documents for your sites
          </p>
        </div>
        <Button
          onClick={() => setRagDialogOpen(true)}
          variant="outline"
          className="flex items-center gap-2"
        >
          <Database className="h-4 w-4" />
          View RAG Documents
        </Button>
      </div>

      {/* Site and Status Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Select Site</label>
              <Select value={selectedSite} onValueChange={setSelectedSite}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a site" />
                </SelectTrigger>
                <SelectContent>
                  {sites.map((site) => (
                    <SelectItem key={site.id} value={site.id}>
                      {site.name} - {site.url}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <label className="text-sm font-medium mb-2 block">Status Filter</label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="hold">On Hold</SelectItem>
                  <SelectItem value="researching">Researching</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {statsCards.map((stat) => (
          <Card key={stat.title}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
              <stat.icon className={`h-4 w-4 ${stat.color}`} />
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${stat.color}`}>
                {stat.count}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Topics Table */}
      <Card>
        <CardHeader>
          <CardTitle>Topics</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading topics...</div>
          ) : topics.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No topics found for the selected filters.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Topic Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Relevance</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Research</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {topics.map((topic) => (
                  <TableRow key={topic.id}>
                    <TableCell className="font-medium">{topic.topic_name}</TableCell>
                    <TableCell className="max-w-md">
                      <p className="truncate">{topic.topic_description}</p>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{ width: `${topic.relevance_score * 100}%` }}
                          />
                        </div>
                        <span className="text-sm text-muted-foreground">
                          {(topic.relevance_score * 100).toFixed(0)}%
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(topic.status)}</TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleViewResearch(topic)}
                        className="flex items-center gap-1"
                      >
                        <Eye className="h-3 w-3" />
                        View
                      </Button>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        {topic.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleReview(topic, 'approve')}
                              className="text-green-600 hover:text-green-700"
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleReview(topic, 'reject')}
                              className="text-red-600 hover:text-red-700"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleReview(topic, 'hold')}
                              className="text-gray-600 hover:text-gray-700"
                            >
                              <Clock className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {reviewAction === 'approve' && 'Approve Topic'}
              {reviewAction === 'reject' && 'Reject Topic'}
              {reviewAction === 'hold' && 'Put Topic on Hold'}
            </DialogTitle>
            <DialogDescription>
              {selectedTopic?.topic_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Notes (optional)</label>
              <Textarea
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder="Add any notes about this decision..."
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={submitReview}>
              {reviewAction === 'approve' && 'Approve'}
              {reviewAction === 'reject' && 'Reject'}
              {reviewAction === 'hold' && 'Put on Hold'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Research Documents Dialog */}
      <Dialog open={researchDialogOpen} onOpenChange={setResearchDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Research Documents</DialogTitle>
            <DialogDescription>
              {selectedTopic?.topic_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {loadingResearch ? (
              <div className="text-center py-8">Loading research documents...</div>
            ) : topicResearch.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No research documents found for this topic.
              </div>
            ) : (
              topicResearch.map((research) => (
                <Card key={research.id}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <CardTitle className="text-lg">{research.title}</CardTitle>
                      <Badge className={research.is_approved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                        {research.is_approved ? 'Approved' : 'Pending'}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {/* Full Research Content */}
                      <div>
                        <h4 className="font-semibold mb-2">Research Content</h4>
                        <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                          <div 
                            className="prose prose-sm max-w-none text-sm leading-relaxed prose-headings:font-bold prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-ul:list-disc prose-ul:ml-4 prose-li:ml-4 prose-strong:font-semibold prose-a:text-blue-600 prose-a:underline hover:prose-a:text-blue-800"
                            dangerouslySetInnerHTML={{ 
                              __html: research.content || 'No research content available.'
                            }}
                          />
                        </div>
                      </div>
                    {research.key_findings && (
                      <div className="mt-4 p-3 bg-gray-50 rounded">
                        <p className="text-sm font-medium mb-1">Key Findings:</p>
                        <div className="text-sm space-y-1">
                          {(() => {
                            try {
                              const findings = typeof research.key_findings === 'string' 
                                ? JSON.parse(research.key_findings) 
                                : research.key_findings;
                              
                              if (findings.findings && Array.isArray(findings.findings)) {
                                return findings.findings.map((finding: string, idx: number) => (
                                  <div key={idx} className="flex items-start">
                                    <span className="mr-2">•</span>
                                    <span>{finding}</span>
                                  </div>
                                ));
                              }
                              return <p>{JSON.stringify(findings, null, 2)}</p>;
                            } catch (e) {
                              return <p>{String(research.key_findings)}</p>;
                            }
                          })()}
                        </div>
                      </div>
                    )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* RAG Documents Dialog */}
      <Dialog open={ragDialogOpen} onOpenChange={setRagDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>RAG Documents for {sites.find(s => s.id === selectedSite)?.name}</DialogTitle>
            <DialogDescription>
              These documents are used by the AI chatbot to provide context-aware responses
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {loadingRAG ? (
              <div className="text-center py-8">Loading RAG documents...</div>
            ) : ragDocuments.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No RAG documents found for this site.
              </div>
            ) : (
              <div className="space-y-4">
                <div className="text-sm text-muted-foreground">
                  Total documents: {ragDocuments.length}
                </div>
                {ragDocuments.map((doc) => (
                  <Card key={doc.id} className="overflow-hidden">
                    <CardHeader className="pb-3">
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-2">
                          {getRAGTypeIcon(doc.type)}
                          <CardTitle className="text-base">{doc.name}</CardTitle>
                        </div>
                        <div className="flex items-center gap-2">
                          {getRAGTypeBadge(doc.type)}
                          <Badge variant="outline">
                            {(doc.relevance * 100).toFixed(0)}% relevant
                          </Badge>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground mb-2">
                        {doc.content.substring(0, 200)}...
                      </p>
                      {doc.keywords && doc.keywords.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {doc.keywords.map((keyword, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">
                              {keyword}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}